import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';

interface EditWebsiteContactModalProps {
  onClose: () => void;
  onSubmit: (data: { website?: string }) => Promise<void>;
  initialData: {
    website?: string;
  };
}

export const EditWebsiteContactModal: React.FC<EditWebsiteContactModalProps> = ({
  onClose,
  onSubmit,
  initialData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [website, setWebsite] = useState(initialData.website || '');

  const handleSubmit = async () => {
    setModalError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        website: website.trim() || undefined
      });
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to update website');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  const isValidUrl = (url: string) => {
    if (!url) return true; // Optional field
    try {
      new URL(url);
      return true;
    } catch {
      return url.includes('.') && !url.includes(' ');
    }
  };

  const isFormValid = () => {
    return website.trim().length === 0 || isValidUrl(website);
  };

  const hasChanges = () => {
    return website !== (initialData.website || '');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Edit Website & Contact</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Website (Optional)</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://company.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
            <p className="text-white/40 text-xs mt-1">
              Your organization's public website
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!isFormValid() || !hasChanges() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};